import { jsx as _jsx } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef, } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useApiClient, ApiClientService, AuthContext } from 'libs.react_internal';
export const DataReportsContext = createContext({
    activeReportsTab: 0,
    setActiveReportsTab: () => { },
    activeStudyConfigVersion: null,
    drilldownLevel: 0,
    setDrilldownLevel: () => { },
    libRequestsParams: {},
    selectedTestEnv: { label: '', value: '' },
    setSelectedTestEnv: () => { },
    devEnvironments: [],
    prodEnvironments: [],
    isInitialized: false,
    isLoadingEnvironments: false,
    hasProductionEnvironments: false,
    studyId: '',
    setStudyId: () => { },
    testEnvironmentsOptions: [],
    triggerCortexLogin: () => Promise.resolve(),
    setIsCortexLoginSettled: () => { },
    fetchAuthPolicies: () => Promise.resolve(),
    authPolicies: [],
    hasPolicy: () => false,
    showFullTimestamp: false,
    // Default ON: value formatting applies everywhere (library preview, builders, consumers rendered
    // outside a DataReportsProvider). Only the provider on the legacy study-report route flips it off.
    applyValueFormatting: true,
});
export const DataReportsProvider = ({ children }) => {
    const iamClient = useApiClient(ApiClientService.IAM);
    const momClient = useApiClient(ApiClientService.MOM);
    const { entityId } = useContext(AuthContext);
    const { user } = useContext(AuthContext);
    const [drilldownLevel, setDrilldownLevel] = useState(0);
    const [activeReportsTab, setActiveReportsTab] = useState(0);
    const [selectedTestEnv, setSelectedTestEnv] = useState({ label: '', value: '' });
    const [studyId, setStudyId] = useState('');
    const [isInitialized, setIsInitialized] = useState(false);
    const [isCortexLoginSettled, setIsCortexLoginSettled] = useState(false);
    const [authPolicies, setAuthPolicies] = useState([]);
    const cortexLoginInProgressRef = useRef(false);
    const [searchParams] = useSearchParams();
    const { pathname } = useLocation();
    const env = searchParams.get('env');
    const rid = searchParams.get('rid');
    const getEnvsData = async (isProd = false) => {
        const params = {
            withWorkflows: true,
            withCortexAccess: true,
        };
        if (isProd) {
            params.clusterTypes = 'prod';
        }
        else {
            params.clusterTypes = 'dev';
        }
        const { data: response } = await momClient.get(`/v1/entities/${entityId}/studies/${studyId}/environments`, {
            params,
        });
        return response.data;
    };
    // Query key includes `cortex` to keep this cache slot distinct from
    // hooks that hit the same endpoint without `withCortexAccess` (e.g.
    // useStudyProdDeployments on the Deploy page). Sharing a cache key with
    // those hooks would leak environments the user does not have cortex
    // access to into `activeStudyConfigVersion`.
    const { data: devEnvironments = [], isLoading: isLoadingDevEnvironments } = useQuery({
        queryKey: ['entities', entityId, 'studies', studyId, 'environments', 'dev', 'cortex'],
        queryFn: () => getEnvsData(false),
        enabled: !!entityId && !!studyId,
    });
    const { data: prodEnvironments = [], isLoading: isLoadingProdEnvironments } = useQuery({
        queryKey: ['entities', entityId, 'studies', studyId, 'environments', 'prod', 'cortex'],
        queryFn: () => getEnvsData(true),
        enabled: !!entityId && !!studyId,
    });
    const hasProductionEnvironments = useMemo(() => prodEnvironments.length > 0, [prodEnvironments]);
    const activeStudyConfigVersion = useMemo(() => {
        if (activeReportsTab === 0) {
            if (!selectedTestEnv.value) {
                return null;
            }
            const selectedDevEnvironment = devEnvironments.find((environment) => environment.cortexId === selectedTestEnv.value);
            return selectedDevEnvironment?.metadata?.studyConfigVersion ?? null;
        }
        // Use the highest version across prod envs so that newly added reports become visible
        // as soon as ANY prod env is upgraded — regions that lag (e.g. China) shouldn't hold
        // visibility back. Filter out undefined/NaN to avoid `Math.max` returning NaN.
        const versions = prodEnvironments
            .map((environment) => environment.metadata?.studyConfigVersion)
            .filter((v) => typeof v === 'number' && !Number.isNaN(v));
        if (!versions.length) {
            return null;
        }
        return Math.max(...versions);
    }, [activeReportsTab, selectedTestEnv.value, devEnvironments, prodEnvironments]);
    const testEnvironmentsOptions = useMemo(() => devEnvironments
        ?.filter((env) => env.deployment.type !== 'ephemeral')
        .map((env) => ({
        label: env.name,
        value: env.cortexId,
    })), [devEnvironments]);
    useEffect(() => {
        if (devEnvironments?.length > 0 || prodEnvironments?.length > 0) {
            setIsInitialized(true);
        }
    }, [devEnvironments, prodEnvironments]);
    const hasSetDefaultTabRef = useRef(false);
    useEffect(() => {
        if (!hasSetDefaultTabRef.current && prodEnvironments.length > 0) {
            setActiveReportsTab(1);
            hasSetDefaultTabRef.current = true;
        }
    }, [prodEnvironments]);
    useEffect(() => {
        cortexLoginInProgressRef.current = false;
        setIsCortexLoginSettled(false);
    }, [devEnvironments, selectedTestEnv.value, env]);
    const libRequestsParams = useMemo(() => {
        // When a specific library resource id (rid) is targeted via URL, fetch that
        // exact version by id only — omit latest/status so we don't inadvertently
        // filter it out.
        if (rid) {
            return {};
        }
        return { latest: true, status: 'PUBLISHED' };
    }, [rid]);
    // Study reports opened with `rid` in the URL come from the new-flow (library
    // dashboards); legacy reports (data_reporting) have no `rid`. Downstream table
    // charts and CSV exports use this to decide TIME dimension formatting (SSP-4116).
    const showFullTimestamp = !!rid;
    // Cube value formatting — percent / abbreviation / decimals / axis-tick + tooltip formatting and the
    // formatted-export option — applies everywhere EXCEPT the legacy report-dashboard viewer opened
    // without a `rid`. The viewer routes across apps share the `/reports/:id` segment
    // (study_management `/data/reports/:id`, site_app `/site/study/:id/reports/:id`); Library
    // preview/builder routes (`/library/...`) do not, so they keep full formatting. New-flow reports
    // (with `rid`) format; only the legacy no-`rid` viewer opts out to render raw values as it did
    // before those features landed (SSP-3972 / -4129 / -4105).
    const isLegacyReportView = pathname.includes('/reports/') && !rid;
    const applyValueFormatting = !isLegacyReportView;
    const isLoadingEnvironments = useMemo(() => {
        return isLoadingDevEnvironments || isLoadingProdEnvironments;
    }, [isLoadingDevEnvironments, isLoadingProdEnvironments]);
    const getCortexEndpointUrl = (env) => {
        return `${env.cortexURL}/${env.code}`;
    };
    const loginToCortex = useCallback(async (env) => {
        try {
            const response = await fetch(`${getCortexEndpointUrl(env)}/v2/accounts/status?__no_redirect__=1&expand=true`, {
                headers: {
                    'Medable-Client-Key': 'c_default_app',
                },
                credentials: 'include',
            });
            const { data } = await response.json();
            if (data?.loggedin && user?.email === data?.account?.email) {
                return;
            }
            await iamClient.get(`/v1/sso/token?aud=${getCortexEndpointUrl(env)}`, {
                headers: {
                    'X-SVC-Version': undefined,
                },
                withCredentials: true,
            });
        }
        catch (error) {
            console.error('Error logging in to Cortex:', error);
        }
    }, [iamClient, user?.email]);
    const triggerCortexLogin = useCallback(async () => {
        if (isLoadingEnvironments || isCortexLoginSettled || cortexLoginInProgressRef.current) {
            return;
        }
        cortexLoginInProgressRef.current = true;
        const effectiveEnv = env || (activeReportsTab === 0 ? 'test' : 'prod');
        try {
            if (effectiveEnv === 'test' && selectedTestEnv.value) {
                const foundEnv = devEnvironments.find((envItem) => envItem.cortexId === selectedTestEnv.value);
                if (foundEnv) {
                    await loginToCortex(foundEnv);
                }
            }
            if (effectiveEnv === 'prod' && hasProductionEnvironments) {
                const loginPromises = prodEnvironments.map((environment) => loginToCortex(environment));
                await Promise.all(loginPromises);
            }
        }
        catch (error) {
            console.error('Error triggering Cortex login:', error);
        }
        finally {
            cortexLoginInProgressRef.current = false;
            setIsCortexLoginSettled(true);
        }
    }, [
        isLoadingEnvironments,
        isCortexLoginSettled,
        env,
        activeReportsTab,
        selectedTestEnv.value,
        devEnvironments,
        hasProductionEnvironments,
        prodEnvironments,
        loginToCortex,
    ]);
    const fetchAuthPolicies = useCallback(async (targetStudyId) => {
        const effectiveStudyId = targetStudyId || studyId;
        if (!user?.id || !entityId || !effectiveStudyId) {
            return;
        }
        try {
            const { data } = await iamClient.get(`/v1/identities/${user?.id}/policies?resource=entity/${entityId}/study/${effectiveStudyId}&includeOwn=true`);
            const policies = data?.data?.map((policy) => policy.role) ?? [];
            setAuthPolicies(policies);
        }
        catch (error) {
            console.error('Error fetching auth policies:', error);
        }
    }, [iamClient, entityId, studyId, user?.id]);
    const hasPolicy = useCallback((policy) => {
        return authPolicies.includes(policy);
    }, [authPolicies]);
    const value = useMemo(() => ({
        activeReportsTab,
        setActiveReportsTab,
        activeStudyConfigVersion,
        drilldownLevel,
        setDrilldownLevel,
        libRequestsParams,
        selectedTestEnv,
        setSelectedTestEnv,
        devEnvironments,
        prodEnvironments,
        isInitialized,
        isLoadingEnvironments,
        hasProductionEnvironments,
        studyId,
        setStudyId,
        testEnvironmentsOptions,
        triggerCortexLogin,
        setIsCortexLoginSettled,
        fetchAuthPolicies,
        authPolicies,
        hasPolicy,
        showFullTimestamp,
        applyValueFormatting,
    }), [
        activeReportsTab,
        setActiveReportsTab,
        activeStudyConfigVersion,
        drilldownLevel,
        setDrilldownLevel,
        libRequestsParams,
        selectedTestEnv,
        setSelectedTestEnv,
        devEnvironments,
        prodEnvironments,
        isInitialized,
        isLoadingEnvironments,
        hasProductionEnvironments,
        studyId,
        setStudyId,
        testEnvironmentsOptions,
        triggerCortexLogin,
        setIsCortexLoginSettled,
        fetchAuthPolicies,
        hasPolicy,
        showFullTimestamp,
        applyValueFormatting,
    ]);
    return _jsx(DataReportsContext.Provider, { value: value, children: children });
};
